import { useMutation } from "@tanstack/react-query";
import { useAuth } from "../../contexts/auth/Context";
import { userLoginRequest } from "../requests/auth";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../contexts/toast/Context";
import { decodeUserInfo, isSuperAdmin } from "../../utils/user";
import {
  EmailNotVerifiedError,
  GymSelectionRequiredError,
  type AuthFormInput,
  type AvailableGym,
} from "../../utils/zod/auth";

interface UseLoginResult {
  mutate: ReturnType<
    typeof useMutation<unknown, Error, AuthFormInput>
  >["mutate"];
  isPending: boolean;
  availableGyms: AvailableGym[] | null;
  emailNotVerified: boolean;
  resetGymSelection: () => void;
}

export const useLogin = (): UseLoginResult => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { login } = useAuth();

  const mutation = useMutation({
    mutationFn: (user: AuthFormInput) => userLoginRequest(user),
    meta: { track: true, eventName: "auth:login" },
    onSuccess: (response_data) => {
      const { token, mustChangePassword, mustAcceptTerms } = response_data;
      const { role } = decodeUserInfo(token);

      login({
        token,
        mustChangePassword: Boolean(mustChangePassword),
        mustAcceptTerms: Boolean(mustAcceptTerms),
      });
      showToast("Sesión iniciada correctamente", "success");
      // El login del panel ya no autentica entrenadores (tienen su propia puerta
      // en `/auth/trainer`), así que acá solo caen roles del panel.
      const redirectPath = isSuperAdmin(role) ? "/admin/dashboard" : "/";
      navigate(redirectPath, { replace: true });
    },
    onError: (error: Error) => {
      // Las dos se muestran en el formulario, con su propia accion: elegir gym o
      // reenviar la verificacion. Un toast rojo las dejaria sin salida.
      if (error instanceof GymSelectionRequiredError) return;
      if (error instanceof EmailNotVerifiedError) return;
      showToast(error.message, "error");
    },
  });

  const availableGyms =
    mutation.error instanceof GymSelectionRequiredError
      ? mutation.error.availableGyms
      : null;

  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    availableGyms,
    emailNotVerified: mutation.error instanceof EmailNotVerifiedError,
    resetGymSelection: mutation.reset,
  };
};
